// frontend/src/actions/session_actions.js
import * as APIUtil from "../util/session_api_util";
import { jwtDecode } from "jwt-decode";
import { emitSessionExpired } from "../util/auth_events";

export const RECEIVE_CURRENT_USER = "RECEIVE_CURRENT_USER";
export const RECEIVE_SESSION_ERRORS = "RECEIVE_SESSION_ERRORS";
export const RECEIVE_USER_LOGOUT = "RECEIVE_USER_LOGOUT";
export const RECEIVE_USER_SIGN_IN = "RECEIVE_USER_SIGN_IN";

export const SET_SESSION_EXPIRED = "SET_SESSION_EXPIRED";
export const CLEAR_SESSION_EXPIRED = "CLEAR_SESSION_EXPIRED";
export const SESSION_HYDRATE_START = "SESSION_HYDRATE_START";
export const SESSION_HYDRATE_DONE = "SESSION_HYDRATE_DONE";


export const setSessionExpired = (reason = "expired") => ({
  type: SET_SESSION_EXPIRED,
  reason,
});

export const clearSessionExpired = () => ({
  type: CLEAR_SESSION_EXPIRED,
});

/* =========== action creators =========== */

export const receiveCurrentUser = (currentUser) => ({
  type: RECEIVE_CURRENT_USER,
  currentUser,
});

export const receiveUserSignIn = () => ({
  type: RECEIVE_USER_SIGN_IN,
});

export const receiveSessionErrors = (errors) => ({
  type: RECEIVE_SESSION_ERRORS,
  errors,
});

export const logoutUser = () => ({
  type: RECEIVE_USER_LOGOUT,
});

export const sessionHydrateStart = () => ({ type: SESSION_HYDRATE_START });
export const sessionHydrateDone = () => ({ type: SESSION_HYDRATE_DONE });


/* =========== soft-expire thunk =========== */
// ✅ Single source of truth for “session expired”
export const softExpireSession = (reason = "expired") => (dispatch) => {
  // save where they were (HashRouter)
  try {
    const returnTo = window.location.hash || "#/dashboard";
    sessionStorage.setItem("returnTo", returnTo);
  } catch (e) {}

  // clear token + auth header
  try {
    localStorage.removeItem("jwtToken");
  } catch (e) {}

  APIUtil.setAuthToken(false);

  // mark expired (do NOT hard logout)
  dispatch(setSessionExpired(reason));

  // show modal overlay
  emitSessionExpired(reason);
};

/* =========== thunks =========== */

// HYDRATE: after refresh, replace decoded token user with full DB user
export const hydrateSessionFromDashboard = () => async (dispatch) => {
  dispatch(sessionHydrateStart());

  try {
    const res = await APIUtil.fetchDashboardUser();
    dispatch(receiveCurrentUser(res.data));
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    console.error("Error hydrating user from /dashboard:", err);

    if (status === 401) {
      dispatch(softExpireSession("401")); // ✅ must dispatch
    }

    throw err;
  } finally {
    dispatch(sessionHydrateDone());
  }
};


// ✅ Avatar upload thunk that MERGES returned user into existing session user
export const uploadAvatar = (userId, file) => async (dispatch, getState) => {
  try {
    const formData = new FormData();
    formData.append("avatar", file);

    const token = localStorage.getItem("jwtToken");
    const headers = {};
    if (token) headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;

    const res = await fetch(`/api/users/${userId}/avatar`, {
      method: "POST",
      headers,
      body: formData,
      credentials: "include",
    });

    if (!res.ok) throw new Error(`Avatar upload failed with status ${res.status}`);

    const data = await res.json();

    if (data?.user) {
      const prev = getState()?.session?.user || {};
      const merged = { ...prev, ...data.user };
      dispatch(receiveCurrentUser(merged));
    }

    return data;
  } catch (err) {
    console.error("Avatar upload error:", err);
    throw err;
  }
};

// SIGNUP: just marks signedIn = true, does NOT log the user in
export const signup = (user) => (dispatch) => {
  return APIUtil.signup(user)
    .then((res) => {
      dispatch(receiveUserSignIn());
      return res?.data;
    })
    .catch((err) => {
      const payload = err?.response?.data || { signup: "Signup failed. Please try again." };
      dispatch(receiveSessionErrors(payload));
      throw err;
    });
};

// LOGIN
export const login = (user) => (dispatch) => {
  return APIUtil.login(user)
    .then(async (res) => {
      const bearer = res.data.token; // "Bearer xxx.yyy.zzz"

      localStorage.setItem("jwtToken", bearer);
      APIUtil.setAuthToken(bearer);
window.__COSMETINK_SOFT_EXPIRED__ = false;
      const rawJwt = bearer.replace(/^Bearer\s+/i, "");
      const decoded = jwtDecode(rawJwt);

      dispatch(receiveCurrentUser(decoded));
      dispatch(clearSessionExpired());

      await dispatch(hydrateSessionFromDashboard());

      // redirect back if we saved a returnTo
      try {
        const back = sessionStorage.getItem("returnTo");
        if (back) {
          sessionStorage.removeItem("returnTo");
          window.location.hash = back.startsWith("#") ? back : `#${back}`;
        }
      } catch (e) {}

      return decoded;
    })
    .catch((err) => {
      const payload = err?.response?.data || { login: "Login failed. Please try again." };
      dispatch(receiveSessionErrors(payload));
      throw err;
    });
};

// LOGOUT (manual logout stays hard)
export const logout = () => (dispatch) => {
  localStorage.removeItem("jwtToken");
  APIUtil.setAuthToken(false);
  window.__COSMETINK_SOFT_EXPIRED__ = false;
  dispatch(logoutUser());
};
